import React from "react";
import { number, shape, string } from "prop-types";
import { compose, isFunc } from "@insightfulscience/shared-utils/fn";
import query from "../url/query";
import displayName from "../displayName";
import ApplicationError from "../ApplicationError";

const getCurrentPath = ({ asPath }) => asPath;

const getQueryParam = (paramName = "throw") => pathname => query(pathname)[paramName];

const parseErrorParams = wsParamValue => /(-?\d+)-([^-].*)/.exec(wsParamValue) || [];

const normalizeParams = ([, code, message]) => [code && parseInt(code, 10), message];

const getErrorParamsFromPath = paramName =>
	compose(
		normalizeParams, //
		parseErrorParams,
		getQueryParam(paramName),
		getCurrentPath,
	);

export const throwError = (getInitialProps, paramName) => (context, container) => {
	const [code, message] = getErrorParamsFromPath(paramName)(context);
	if (code == null) {
		return isFunc(getInitialProps) ? getInitialProps(context, container) : { __throwError: true };
	}

	if (code < 0)
		throw new ApplicationError(
			new Error(`Server Side: ${message}`),
			-code,
			`Server Side: ${message}`,
		);

	return { errorToThrow: { code, message } };
};

/** @type {(paramName: string) => <P extends {}>(Component: React.Component<P>) => React.FC<P>} */
const withForceErrorHOC = paramName => Component => {
	const ComponentWithThrowError = ({ errorToThrow, ...props }) => {
		if (errorToThrow && typeof window !== "undefined")
			throw new ApplicationError(
				new Error(`Client Side: ${errorToThrow.message}`),
				errorToThrow.code,
				`Client Side: ${errorToThrow.message}`,
			);
		return <Component {...props} />;
	};

	Object.assign(ComponentWithThrowError, Component);

	ComponentWithThrowError.propTypes = {
		...Component.propTypes,
		errorToThrow: shape({
			code: number,
			message: string,
		}),
	};

	ComponentWithThrowError.displayName = displayName("withForceError", Component);
	ComponentWithThrowError.getInitialProps = throwError(Component.getInitialProps, paramName);
	return ComponentWithThrowError;
};

export const withForceError = withForceErrorHOC;
